import { useMemo } from "react";

import { isEmpty, map, orderBy } from "lodash/fp";

import UpsellModule, {
  UpsellModuleCarousel,
  UpsellModuleProps,
} from "@app/analytics/components/UpsellModule";
import type { UseProductivityIntegrationStatus } from "@app/analytics/hooks/integrations/useProductivityIntegrationStatus";
import theme from "@app/analytics/theme";
import useAuth from "@app/common/auth/useAuth";
import useWindowDimensions from "@app/common/hooks/useWindowDimensions";
import ROUTES from "@app/common/routes";
import {
  ProductivityIntegrationStatus,
  ProductivityIntegrationStatusStep,
  User,
} from "@app/common/types";

import ReadoutStatusContent from "./ReadoutStatusContent";

const MAX_MODULES = {
  lg: 3,
  sm: 2,
};

const generateSlide = (
  status: ProductivityIntegrationStatus,
  opts: {
    userId: User["id"];
    onDismiss: (provider: ProductivityIntegrationStatus["provider"]) => void;
  }
): UpsellModuleProps & { key: string; step: number } => {
  const isComplete = status.step === ProductivityIntegrationStatusStep.COMPLETE;

  return {
    key: status.provider,
    step: status.step,
    color: "#4C47CC0A", // = shades.primary.500 @ 4% opacity
    cta: status.error
      ? undefined
      : {
          label: "View Readout",
          disabled: !isComplete,
          to: isComplete
            ? ROUTES.topicReportById(status.topicReportIds[0], opts.userId)
            : undefined,
        },
    onDismiss: () => opts.onDismiss(status.provider),
    children: <ReadoutStatusContent status={status} step={status.step} />,
  };
};

interface ReadoutStatusProps extends UseProductivityIntegrationStatus {
  additionalModules: number;
}

const ReadoutStatus = ({
  additionalModules,
  statuses,
  onDismissProviderStatus,
}: ReadoutStatusProps) => {
  const { user } = useAuth();
  const { windowWidth } = useWindowDimensions();

  const carouselThreshold =
    (windowWidth > theme.breakpoints.values.lg
      ? MAX_MODULES.lg
      : MAX_MODULES.sm) - additionalModules;
  const showAsCarousel = statuses.length > carouselThreshold;

  const slides = useMemo(
    () =>
      map(
        (status) =>
          generateSlide(status, {
            userId: user!.id,
            onDismiss: onDismissProviderStatus,
          }),
        statuses
      ),
    [statuses, onDismissProviderStatus]
  );

  if (isEmpty(statuses) || !user?.id) return null;

  if (showAsCarousel) {
    // when showing as a carousel, prioritize the ones that are
    // farther along in the process
    const orderedSlides = orderBy("step", "desc", slides);
    return <UpsellModuleCarousel slides={orderedSlides} />;
  }

  return (
    <>
      {slides.map(({ key, ...props }) => {
        return <UpsellModule key={key} {...props} />;
      })}
    </>
  );
};

export default ReadoutStatus;
